import {AuthenticatedSessionState} from "auth/AuthenticatedSessionWaiter";
import {nextUpgradePlan, planOf} from "home/gating";
import {useAuthenticatedSession, useUser} from "../UserProvider";
import {
	limitUpsell, shouldAskForLimitOffer, type FirstMonthOffer, visibleOfferSpotsLeft,
} from "./firstMonthOffer";
import {useFirstMonthOffer} from "./useFirstMonthOffer";
import type {PaidPlanId} from "pricing/planCatalogue";

/** What the hit-a-limit moment needs in order to render itself. */
export interface LimitOffer {
	offer: FirstMonthOffer | null;
	plan: PaidPlanId | null;
	occasion: string | null;
	endsOn: string | null;
	spotsLeft: number | null;
}

/**
 * The 50%-off offer at the moment a Free reader hits their monthly download cap
 * (kitecorp/transferify-ui#1450) — the fourth moment of kitecorp/transferify#117.
 *
 * <b>No dismissal, unlike the other moments.</b> This one has no callout of its own to close: it rides
 * the download-usage banner that was already on screen, whose own visibility rule
 * (`account/downloadUsage`) decides when it appears and disappears. A second dismissal here would let
 * a reader close the OFFER while the banner it lives in stayed, leaving a CTA whose label had silently
 * lost its reason.
 *
 * <b>The trigger is the cap being HIT.</b> `capReached` comes from the banner's existing level, so this
 * introduces no limit logic of its own — the ticket's constraint, and the reason it is a parameter
 * rather than something recomputed here.
 *
 * @param capReached the download-usage view reports the cap reached, not merely approached
 */
export function useLimitOffer(capReached: boolean): LimitOffer {
	const [user] = useUser();
	const [session] = useAuthenticatedSession();
	const plan = planOf(user);
	const target = nextUpgradePlan(plan);
	// Gated on the authenticated session like every other moment: the offer endpoint is authenticated,
	// and an ask made before the auth listener settles answers 401 and is swallowed.
	const quoted = useFirstMonthOffer(target,
		shouldAskForLimitOffer(capReached, plan) && session === AuthenticatedSessionState.Authenticated);
	const offer = limitUpsell({capReached, plan, quotedPercentOff: quoted?.percentOff ?? null});
	return {
		offer,
		plan: target,
		occasion: offer === null ? null : quoted?.occasion ?? null,
		endsOn: offer === null ? null : quoted?.endsOn ?? null,
		spotsLeft: offer === null ? null : visibleOfferSpotsLeft(quoted?.spotsLeft),
	};
}
